import { customElement, scss } from '../../core/ids-decorators';
import { attributes } from '../../core/ids-attributes';
import IdsElement from '../../core/ids-element';
import styles from './ids-layout-grid.scss';
import { stringToBool } from '../../utils/ids-string-utils/ids-string-utils';

import {
  ALIGN_ITEMS,
  ALIGN_TYPES,
  GRID_ATTRIBUTES,
  GAP_TYPES,
  FLOW_TYPES,
  MARGIN_SIZES,
  MAX_WIDTH_SIZES,
  PADDING_SIZES,
  prefix
} from './ids-layout-grid-common';

const OVERFLOW_CSS = ['visible', 'hidden', 'scroll', 'auto', 'clip', 'inherit', 'initial', 'unset'];

/**
 * IDS Layout Grid Component
 * @type {IdsLayoutGrid}
 * @inherits IdsElement
 */
@customElement('ids-layout-grid')
@scss(styles)
export default class IdsLayoutGrid extends IdsElement {
  /**
   * Set the grid align-content
   * @param {string | null} value The align-content [null, start, end, between, around, evenly]
   */
  set alignContent(value: string | null) {
    if (!value || ALIGN_TYPES.indexOf(value as any) <= 0) {
      this.removeAttribute(attributes.ALIGN_CONTENT);
    } else {
      this.setAttribute(attributes.ALIGN_CONTENT, value);
    }
  }

  /**
   * Get the grid align-content setting
   * @returns {string | null} The align-content [null, start, end, between, around, evenly]
   */
  get alignContent(): string | null { return this.getAttribute(attributes.ALIGN_CONTENT); }

  /**
   * Set the grid align-items
   * @param {string | null} value The align-items [null, start, end, center, center-inputs, stretch]
   */
  set alignItems(value: string | null) {
    if (!value || ALIGN_ITEMS.indexOf(value as any) < 0) {
      this.removeAttribute(attributes.ALIGN_ITEMS);
    } else {
      this.setAttribute(attributes.ALIGN_ITEMS, value);
    }
    this.#syncChildFieldHeight();
  }

  /**
   * Get the grid align-items setting
   * @returns {string | null} The align-items [null, start, end, center, center-inputs, stretch]
   */
  get alignItems(): string | null { return this.getAttribute(attributes.ALIGN_ITEMS); }

  /**
   * Set auto-fit attribute
   * @param {boolean} value boolean
   */
  set autoFit(value: boolean) {
    const isTruthy = stringToBool(value);
    if (isTruthy) {
      this.setAttribute(attributes.AUTO_FIT, '');
    } else {
      this.removeAttribute(attributes.AUTO_FIT);
    }
  }

  /**
   * Get auto-fit attribute
   * @readonly
   * @returns {boolean} Indicates whether the auto-fit is enabled
   */
  get autoFit(): boolean | null {
    return stringToBool(this.getAttribute(attributes.AUTO_FIT));
  }

  /**
   * Get the overflow CSS property
   * @returns {string} The current overflow value
   */
  get overflow(): string {
    return this.style.overflow;
  }

  /**
   * Set the overflow CSS property
   * @param {string} value The overflow value [visible, hidden, scroll, auto, clip, inherit, initial, unset]
   */
  set overflow(value: string) {
    if (OVERFLOW_CSS.includes(value)) {
      this.style.overflow = value;
    }
  }

  /**
   * Set auto-fill attribute
   * @param {boolean} value boolean
   */
  set autoFill(value: boolean) {
    const isTruthy = stringToBool(value);
    if (isTruthy) {
      this.setAttribute(attributes.AUTO_FILL, '');
    } else {
      this.removeAttribute(attributes.AUTO_FILL);
    }
  }

  /**
   * Get auto-fill attribute
   * @readonly
   * @returns {boolean} Indicates whether the auto-fill is enabled
   */
  get autoFill(): boolean {
    return stringToBool(this.getAttribute(attributes.AUTO_FILL));
  }

  /**
   * Set columns attribute
   * @param {string | null} value sets the number of columns
   */
  set cols(value: string | null) {
    if (value !== null) {
      this.setAttribute(attributes.COLS, value);
    } else {
      this.removeAttribute(attributes.COLS);
    }
  }

  /**
   * Get columns attribute
   * @readonly
   * @returns {string | null} The number of columns of the grid
   */
  get cols(): string | null { return this.getAttribute(attributes.COLS); }

  /**
   * Set XS columns attribute
   * @param {string | null} value sets the number of columns at the XS breakpoint
   */
  set colsXs(value: string | null) {
    if (value !== null) {
      this.setAttribute(attributes.COLS_XS, value);
    } else {
      this.removeAttribute(attributes.COLS_XS);
    }
  }

  /**
   * Get XS columns attribute
   * @readonly
   * @returns {string | null} The number of columns of the grid at the XS breakpoint
   */
  get colsXs(): string | null { return this.getAttribute(attributes.COLS_XS); }

  /**
   * Set SM columns attribute
   * @param {string | null} value sets the number of columns at the SM breakpoint
   */
  set colsSm(value: string | null) {
    if (value !== null) {
      this.setAttribute(attributes.COLS_SM, value);
    } else {
      this.removeAttribute(attributes.COLS_SM);
    }
  }

  /**
   * Get SM columns attribute
   * @readonly
   * @returns {string | null} The number of columns of the grid at the SM breakpoint
   */
  get colsSm(): string | null { return this.getAttribute(attributes.COLS_SM); }

  /**
   * Set MD columns attribute
   * @param {string | null} value sets the number of columns at the MD breakpoint
   */
  set colsMd(value: string | null) {
    if (value !== null) {
      this.setAttribute(attributes.COLS_MD, value);
    } else {
      this.removeAttribute(attributes.COLS_MD);
    }
  }

  /**
   * Get MD columns attribute
   * @readonly
   * @returns {string | null} The number of columns of the grid at the MD breakpoint
   */
  get colsMd(): string | null { return this.getAttribute(attributes.COLS_MD); }

  /**
   * Set LG columns attribute
   * @param {string | null} value sets the number of columns at the LG breakpoint
   */
  set colsLg(value: string | null) {
    if (value !== null) {
      this.setAttribute(attributes.COLS_LG, value);
    } else {
      this.removeAttribute(attributes.COLS_LG);
    }
  }

  /**
   * Get LG columns attribute
   * @readonly
   * @returns {string | null} The number of columns of the grid at the LG breakpoint
   */
  get colsLg(): string | null { return this.getAttribute(attributes.COLS_LG); }

  /**
   * Set XL columns attribute
   * @param {string | null} value sets the number of columns at the XL breakpoint
   */
  set colsXl(value: string | null) {
    if (value !== null) {
      this.setAttribute(attributes.COLS_XL, value);
    } else {
      this.removeAttribute(attributes.COLS_XL);
    }
  }

  /**
   * Get XL columns attribute
   * @readonly
   * @returns {string | null} The number of columns of the grid at the XL breakpoint
   */
  get colsXl(): string | null { return this.getAttribute(attributes.COLS_XL); }

  /**
   * Set XXL columns attribute
   * @param {string | null} value sets the number of columns at the XXL breakpoint
   */
  set colsXxl(value: string | null) {
    if (value !== null) {
      this.setAttribute(attributes.COLS_XXL, value);
    } else {
      this.removeAttribute(attributes.COLS_XXL);
    }
  }

  /**
   * Get XXL columns attribute
   * @readonly
   * @returns {string | null} The number of columns of the grid at the XXL breakpoint
   */
  get colsXxl(): string | null { return this.getAttribute(attributes.COLS_XXL); }

  /**
   * Set the minColWidth attribute
   * @param {string | null} value Number value that sets the min-width of the grid columns
   */
  set minColWidth(value: string | null) {
    if (value !== null) {
      const newValue = /^\d+(\.\d+)?$/g.test(value) ? `${value}px` : value;
      this.setAttribute(attributes.MIN_COL_WIDTH, newValue);
      this.style.setProperty('--min-col-width', newValue);
    } else {
      this.removeAttribute(attributes.MIN_COL_WIDTH);
      this.style.removeProperty('--min-col-width');
    }
  }

  /**
   * Get the minColWidth attribute
   * @readonly
   * @returns {string | null} The number value that represents the min-height of the grid columns
   */
  get minColWidth(): string | null {
    return this.getAttribute(attributes.MIN_COL_WIDTH);
  }

  /**
   * Set the minRowHeight attribute
   * @param {string | null} value Number value that sets the height of the grid rows
   */
  set minRowHeight(value: string | null) {
    if (value !== null) {
      const newValue = /^\d+(\.\d+)?$/g.test(value) ? `${value}px` : value;
      this.setAttribute(attributes.MIN_ROW_HEIGHT, newValue);
      this.style.setProperty('--min-row-height', newValue);
    } else {
      this.removeAttribute(attributes.MIN_ROW_HEIGHT);
      this.style.removeProperty('--min-row-height');
    }
  }

  /**
   * Get the minRowHeight attribute
   * @readonly
   * @returns {string | null} The number value that represents the min-height of the grid rows
   */
  get minRowHeight(): string | null {
    return this.getAttribute(attributes.MIN_ROW_HEIGHT);
  }

  /**
   * Set the maxColWidth attribute
   * @param {string | null} value Number value that sets the max-width of the grid columns
   */
  set maxColWidth(value: string | null) {
    if (value !== null) {
      const newValue = /^\d+(\.\d+)?$/g.test(value) ? `${value}px` : value;
      this.setAttribute(attributes.MAX_COL_WIDTH, newValue);
      this.style.setProperty('--max-col-width', newValue);
    } else {
      this.removeAttribute(attributes.MAX_COL_WIDTH);
      this.style.removeProperty('--max-col-width');
    }
  }

  /**
   * Get the maxColWidth attribute
   * @readonly
   * @returns {string | null} The number value that represents the max-width of the grid columns
   */
  get maxColWidth(): string | null {
    return this.getAttribute(attributes.MAX_COL_WIDTH);
  }

  /**
   * Set the maxRowHeight attribute
   * @param {string | null} value Number value that sets the max-height of the grid rows
   */
  set maxRowHeight(value: string | null) {
    if (value !== null) {
      const newValue = /^\d+(\.\d+)?$/g.test(value) ? `${value}px` : value;
      this.setAttribute(attributes.MAX_ROW_HEIGHT, newValue);
      this.style.setProperty('--max-row-height', newValue);
    } else {
      this.removeAttribute(attributes.MAX_ROW_HEIGHT);
      this.style.removeProperty('--max-row-height');
    }
  }

  /**
   * Get the maxRowHeight attribute
   * @returns {string | null} The number value that represents the max-height of the grid rows
   */
  get maxRowHeight(): string | null {
    return this.getAttribute(attributes.MAX_ROW_HEIGHT);
  }

  /**
   * Set the grid gap
   * @param {string | null} value The Gap [undefined, none, sm, md, lg, xl]
   */
  set gap(value: string | null) {
    if (!value || GAP_TYPES.indexOf(value as any) <= 0) {
      this.removeAttribute(attributes.GAP);
    } else {
      this.setAttribute(attributes.GAP, value);
    }
  }

  /**
   * Handle The Gap Setting
   * @returns {string | null} The Gap [null, sm, md, lg, xl]
   */
  get gap(): string | null { return this.getAttribute(attributes.GAP); }

  /**
   * Set the row gap
   * @param {string | null} value The Row Gap [undefined, none, sm, md, lg, xl]
   */
  set rowGap(value: string | null) {
    if (!value || GAP_TYPES.indexOf(value as any) <= 0) {
      this.removeAttribute(attributes.ROW_GAP);
    } else {
      this.setAttribute(attributes.ROW_GAP, value);
    }
  }

  /**
   * Handle The Row Gap Setting
   * @returns {string | null} The ROW_GAP [null, sm, md, lg, xl]
   */
  get rowGap(): string | null { return this.getAttribute(attributes.ROW_GAP); }

  /**
   * Set the column gap
   * @param {string | null} value The col Gap [undefined, none, sm, md, lg, xl]
   */
  set colGap(value: string | null) {
    if (!value || GAP_TYPES.indexOf(value as any) <= 0) {
      this.removeAttribute(attributes.COL_GAP);
    } else {
      this.setAttribute(attributes.COL_GAP, value);
    }
  }

  /**
   * Handle The Column Gap Setting
   * @returns {string | null} The col gap [null, sm, md, lg, xl]
   */
  get colGap(): string | null { return this.getAttribute(attributes.COL_GAP); }

  /**
   * Set the margin attribute
   * @param {string | null} value The value of the margin [null, 'sm', 'md', 'lg', 'xl', 'auto']
   */
  set margin(value: string | null) {
    if (!value || MARGIN_SIZES.indexOf(value as any) <= 0) {
      this.removeAttribute(attributes.MARGIN);
    } else {
      this.setAttribute(attributes.MARGIN, value);
    }
  }

  /**
   * Get the margin attribute
   * @returns {string | null} The number value that represents the margin of the grid
   */
  get margin(): string | null { return this.getAttribute(attributes.MARGIN); }

  /**
   * Set the marginY attribute
   * @param {string | null} value The value of the margin-y [null, 'sm', 'md', 'lg', 'xl']
   */
  set marginY(value: string | null) {
    if (!value || MARGIN_SIZES.indexOf(value as any) <= 0) {
      this.removeAttribute(attributes.MARGIN_Y);
    } else {
      this.setAttribute(attributes.MARGIN_Y, value);
    }
  }

  /**
   * Get the margin attribute
   * @returns {string | null} The number value that represents the margin of the grid
   */
  get marginY(): string | null { return this.getAttribute(attributes.MARGIN_Y); }

  /**
   * Set the maxWidth attribute
   * @param {string | null} value The value of the max-width [null, 'xs', 'sm', 'md', 'lg', 'xl', 'xxl', '###px']
   */
  set maxWidth(value: string | null | any) {
    if (MAX_WIDTH_SIZES.indexOf(value as any) >= 0) {
      this.setAttribute(attributes.MAX_WIDTH, value);
      this.style.removeProperty('--max-width');
    } else if (/^\d+$/.test(value)) {
      // If the value is a number without a unit, add 'px'
      value = `${value}px`;
      this.style.setProperty('--max-width', value);
      this.setAttribute(attributes.MAX_WIDTH, value);
    } else if (value?.endsWith('px')) {
      // If the value already has a 'px' unit, use it directly
      this.style.setProperty('--max-width', value);
      this.setAttribute(attributes.MAX_WIDTH, value);
    } else {
      // If the value is invalid or null, remove the attribute and the custom property
      this.removeAttribute(attributes.MAX_WIDTH);
      this.style.removeProperty('--max-width');
    }
  }

  /**
   * Get the maxWidth attribute
   * @returns {string | null} The value that represents the maxWidth of the grid
   */
  get maxWidth(): string | null { return this.getAttribute(attributes.MAX_WIDTH); }

  /**
   * Set the padding attribute
   * @param {string | null} value The value of the padding attribute
   */
  set padding(value: string | null) {
    if (!value || PADDING_SIZES.indexOf(value as any) <= 0) {
      this.removeAttribute(attributes.PADDING);
    } else {
      this.setAttribute(attributes.PADDING, value);
    }
  }

  /**
   * Get the padding attribute
   * @returns {string | null} The number value that represents the padding of the grid
   */
  get padding(): string | null { return this.getAttribute(attributes.PADDING); }

  /**
   * Set the paddingX attribute
   * @param {string | null} value The value of the paddingX attribute
   */
  set paddingX(value: string | null) {
    if (!value || PADDING_SIZES.indexOf(value as any) <= 0) {
      this.removeAttribute(attributes.PADDING_X);
    } else {
      this.setAttribute(attributes.PADDING_X, value);
    }
  }

  /**
   * Get the paddingX attribute
   * @returns {string | null} The number value that represents the paddingX of the grid
   */
  get paddingX(): string | null { return this.getAttribute(attributes.PADDING_X); }

  /**
   * Set the paddingY attribute
   * @param {string | null} value The value of the paddingY attribute
   */
  set paddingY(value: string | null) {
    if (!value || PADDING_SIZES.indexOf(value as any) <= 0) {
      this.removeAttribute(attributes.PADDING_Y);
    } else {
      this.setAttribute(attributes.PADDING_Y, value);
    }
  }

  /**
   * Get the paddingY attribute
   * @returns {string | null} The number value that represents the paddingY of the grid
   */
  get paddingY(): string | null { return this.getAttribute(attributes.PADDING_Y); }

  /**
   * Set the grid justify
   * @param {string | null} value The justify [null, start, end, between, around, evenly]
   */
  set justifyContent(value: string | null) {
    if (!value || ALIGN_TYPES.indexOf(value as any) <= 0) {
      this.removeAttribute(attributes.JUSTIFY_CONTENT);
    } else {
      this.setAttribute(attributes.JUSTIFY_CONTENT, value);
    }
  }

  /**
   * Get the grid justify setting
   * @returns {string | null} The justify [null, start, end, between, around, evenly]
   */
  get justifyContent(): string | null { return this.getAttribute(attributes.JUSTIFY_CONTENT); }

  /**
   * Set the flow attribute of the grid
   * @param { string | null } value Flow [undefined, row, column, dense, row-dense, column-dense]
   */
  set flow(value: string | null) {
    if (!value || FLOW_TYPES.indexOf(value as any) <= 0) {
      this.removeAttribute(attributes.FLOW);
    } else {
      this.setAttribute(attributes.FLOW, value);
    }
  }

  /**
   * Get the flow attribute
   * @returns { string | null } value Flow [undefined, row, column, dense, row-dense, column-dense]
   */
  get flow(): string | null {
    return this.getAttribute(attributes.FLOW);
  }

  /**
   * Set the flow-xs attribute of the grid
   * @param { string | null } value Flow [undefined, row, column, dense, row-dense, column-dense]
   */
  set flowXs(value: string | null) {
    if (!value || FLOW_TYPES.indexOf(value as any) <= 0) {
      this.removeAttribute(attributes.FLOW_XS);
    } else {
      this.setAttribute(attributes.FLOW_XS, value);
    }
  }

  /**
   * Get the flow-xs attribute
   * @returns { string | null } value Flow [undefined, row, column, dense, row-dense, column-dense]
   */
  get flowXs(): string | null {
    return this.getAttribute(attributes.FLOW_XS);
  }

  /**
   * Set the flow-sm attribute of the grid
   * @param { string | null } value Flow [undefined, row, column, dense, row-dense, column-dense]
   */
  set flowSm(value: string | null) {
    if (!value || FLOW_TYPES.indexOf(value as any) <= 0) {
      this.removeAttribute(attributes.FLOW_SM);
    } else {
      this.setAttribute(attributes.FLOW_SM, value);
    }
  }

  /**
   * Get the flow-sm attribute
   * @returns { string | null } value Flow [undefined, row, column, dense, row-dense, column-dense]
   */
  get flowSm(): string | null {
    return this.getAttribute(attributes.FLOW_SM);
  }

  /**
   * Set the flow-md attribute of the grid
   * @param { string | null } value Flow [undefined, row, column, dense, row-dense, column-dense]
   */
  set flowMd(value: string | null) {
    if (!value || FLOW_TYPES.indexOf(value as any) <= 0) {
      this.removeAttribute(attributes.FLOW_MD);
    } else {
      this.setAttribute(attributes.FLOW_MD, value);
    }
  }

  /**
   * Get the flow-md attribute
   * @returns { string | null } value Flow [undefined, row, column, dense, row-dense, column-dense]
   */
  get flowMd(): string | null {
    return this.getAttribute(attributes.FLOW_MD);
  }

  /**
   * Set the flow-lg attribute of the grid
   * @param { string | null } value Flow [undefined, row, column, dense, row-dense, column-dense]
   */
  set flowLg(value: string | null) {
    if (!value || FLOW_TYPES.indexOf(value as any) <= 0) {
      this.removeAttribute(attributes.FLOW_LG);
    } else {
      this.setAttribute(attributes.FLOW_LG, value);
    }
  }

  /**
   * Get the flow-lg attribute
   * @returns { string | null } value Flow [undefined, row, column, dense, row-dense, column-dense]
   */
  get flowLg(): string | null {
    return this.getAttribute(attributes.FLOW_LG);
  }

  /**
   * Set the flow-xl attribute of the grid
   * @param { string | null } value Flow [undefined, row, column, dense, row-dense, column-dense]
   */
  set flowXl(value: string | null) {
    if (!value || FLOW_TYPES.indexOf(value as any) <= 0) {
      this.removeAttribute(attributes.FLOW_XL);
    } else {
      this.setAttribute(attributes.FLOW_XL, value);
    }
  }

  /**
   * Get the flow-xl attribute
   * @returns { string | null } value Flow [undefined, row, column, dense, row-dense, column-dense]
   */
  get flowXl(): string | null {
    return this.getAttribute(attributes.FLOW_XL);
  }

  /**
   * Set the flow-xxl attribute of the grid
   * @param { string | null } value Flow [undefined, row, column, dense, row-dense, column-dense]
   */
  set flowXxl(value: string | null) {
    if (!value || FLOW_TYPES.indexOf(value as any) <= 0) {
      this.removeAttribute(attributes.FLOW_XXL);
    } else {
      this.setAttribute(attributes.FLOW_XXL, value);
    }
  }

  /**
   * Get the flow-xxl attribute
   * @returns { string | null } value Flow [undefined, row, column, dense, row-dense, column-dense]
   */
  get flowXxl(): string | null {
    return this.getAttribute(attributes.FLOW_XXL);
  }

  /**
   * Set the height attribute
   * @param {string | null} value The value of the height attribute
   */
  set height(value: string | null) {
    if (value !== null) {
      // Check if the value contains a unit, if not, default to 'px'
      const newValue = /^\d+(\.\d+)?$/g.test(value) ? `${value}px` : value;
      this.setAttribute(attributes.HEIGHT, newValue);
      this.style.setProperty(attributes.HEIGHT, newValue);
    } else {
      this.removeAttribute(attributes.HEIGHT);
      this.style.removeProperty(attributes.HEIGHT);
    }
  }

  /**
   * Get the height attribute
   * @returns {string | null} The value of the height attribute
   */
  get height(): string | null {
    return this.getAttribute(attributes.HEIGHT);
  }

  /**
   * Set the row attribute
   * @param { string | null } value The amount of rows in the grid
   */
  set rows(value: string | null) {
    if (value !== null) {
      this.setAttribute(attributes.ROWS, value);
    } else {
      this.removeAttribute(attributes.ROWS);
    }
  }

  /**
   * Get the row attribute
   * @returns { string | null } The amount of rows in the grid
   */
  get rows(): string | null {
    return this.getAttribute(attributes.ROWS);
  }

  /**
   * Set the rowXs attribute
   * @param { string | null } value The amount of rows at xs breakpoint in the grid
   */
  set rowsXs(value: string | null) {
    if (value !== null) {
      this.setAttribute(attributes.ROWS_XS, value);
    } else {
      this.removeAttribute(attributes.ROWS_XS);
    }
  }

  /**
   * Get the rowXs attribute
   * @returns { string | null } The amount of rows at xs breakpoint in the grid
   */
  get rowsXs(): string | null {
    return this.getAttribute(attributes.ROWS_XS);
  }

  /**
   * Set the rowSm attribute
   * @param { string | null } value The amount of rows at sm breakpoint in the grid
   */
  set rowsSm(value: string | null) {
    if (value !== null) {
      this.setAttribute(attributes.ROWS_SM, value);
    } else {
      this.removeAttribute(attributes.ROWS_SM);
    }
  }

  /**
   * Get the rowSm attribute
   * @returns { string | null } The amount of rows at sm breakpoint in the grid
   */
  get rowsSm(): string | null {
    return this.getAttribute(attributes.ROWS_SM);
  }

  /**
   * Set the rowMd attribute
   * @param { string | null } value The amount of rows at md breakpoint in the grid
   */
  set rowsMd(value: string | null) {
    if (value !== null) {
      this.setAttribute(attributes.ROWS_MD, value);
    } else {
      this.removeAttribute(attributes.ROWS_MD);
    }
  }

  /**
   * Get the rowMd attribute
   * @returns { string | null } The amount of rows at md breakpoint in the grid
   */
  get rowsMd(): string | null {
    return this.getAttribute(attributes.ROWS_MD);
  }

  /**
   * Set the rowLg attribute
   * @param { string | null } value The amount of rows at lg breakpoint in the grid
   */
  set rowsLg(value: string | null) {
    if (value !== null) {
      this.setAttribute(attributes.ROWS_LG, value);
    } else {
      this.removeAttribute(attributes.ROWS_LG);
    }
  }

  /**
   * Get the rowLg attribute
   * @returns { string | null } The amount of rows at lg breakpoint in the grid
   */
  get rowsLg(): string | null {
    return this.getAttribute(attributes.ROWS_LG);
  }

  /**
   * Set the rowXl attribute
   * @param { string | null } value The amount of rows at xl breakpoint in the grid
   */
  set rowsXl(value: string | null) {
    if (value !== null) {
      this.setAttribute(attributes.ROWS_XL, value);
    } else {
      this.removeAttribute(attributes.ROWS_XL);
    }
  }

  /**
   * Get the rowXl attribute
   * @returns { string | null } The amount of rows at xl breakpoint in the grid
   */
  get rowsXl(): string | null {
    return this.getAttribute(attributes.ROWS_XL);
  }

  /**
   * Set the rowXxl attribute
   * @param { string | null } value The amount of rows at xxl breakpoint in the grid
   */
  set rowsXxl(value: string | null) {
    if (value !== null) {
      this.setAttribute(attributes.ROWS_XXL, value);
    } else {
      this.removeAttribute(attributes.ROWS_XXL);
    }
  }

  /**
   * Get the rowXxl attribute
   * @returns { string | null } The amount of rows at xxl breakpoint in the grid
   */
  get rowsXxl(): string | null {
    return this.getAttribute(attributes.ROWS_XXL);
  }

  /**
   * Set the rowHeight attribute
   * @param { string | null } value The height of the rows in the grid
   */
  set rowHeight(value: string | null) {
    if (value !== null) {
      this.setAttribute(attributes.ROW_HEIGHT, value);
      this.classList.add(`${prefix}-auto-row-height`);
      this.style.setProperty('--grid-auto-row-height', value);
    } else {
      this.removeAttribute(attributes.ROW_HEIGHT);
      this.classList.remove(`${prefix}-auto-row-height`);
      this.style.removeProperty('--grid-auto-row-height');
    }
  }

  /**
   * Get the rowHeight attribute
   * @returns { string | null } The height of the rows in the grid
   */
  get rowHeight(): string | null {
    return this.getAttribute(attributes.ROW_HEIGHT);
  }

  /**
   * Set the templateRows attribute
   * @param { string | null } value The amount of rows in the grid. ex: '1fr 1fr 1fr'
   * @memberof IdsLayoutGrid
   */
  set templateRows(value: string | null) {
    if (value !== null) {
      this.setAttribute(attributes.TEMPLATE_ROWS, value);
      this.classList.add(`${prefix}-template-rows`);
      this.style.setProperty('--grid-template-rows', value);
    } else {
      this.removeAttribute(attributes.TEMPLATE_ROWS);
      this.classList.remove(`${prefix}-template-rows`);
      this.style.removeProperty('--grid-template-rows');
    }
  }

  /**
   * Get the templateRows attribute
   * @readonly
   * @returns {string | null} The amount of rows in the grid
   * @memberof IdsLayoutGrid
   */
  get templateRows(): string | null {
    return this.getAttribute(attributes.TEMPLATE_ROWS);
  }

  /**
   * Set the enable-container attribute
   * @param {string} val The value of the enable-container attribute
   */
  set enableContainer(val: string) {
    const value = stringToBool(val);
    if (value) {
      this.setAttribute(attributes.ENABLE_CONTAINER, '');
      this.classList.add(attributes.ENABLE_CONTAINER);
    } else {
      this.removeAttribute(attributes.ENABLE_CONTAINER);
      this.classList.remove(attributes.ENABLE_CONTAINER);
    }
  }

  /**
   * Get the enable-container attribute
   * @returns {boolean} enable-container
   * @readonly
   */
  get enableContainer(): boolean {
    return this.hasAttribute(attributes.ENABLE_CONTAINER);
  }

  constructor() {
    super();
  }

  static get attributes(): any {
    return GRID_ATTRIBUTES;
  }

  #childObserver?: MutationObserver;

  connectedCallback() {
    super.connectedCallback();
    this.initialSettings();
    this.#syncChildFieldHeight();
    this.#observeChildren();
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.#childObserver?.disconnect();
    this.#childObserver = undefined;
  }

  private initialSettings() {
    this.classList.add(`${prefix}`);
  }

  template(): string {
    return `<slot></slot>`;
  }

  /**
   * For align-items='center-inputs': inspect descendant inputs for their
   * field-height / compact setting and reflect it onto this host so the
   * ids-checkbox can size itself to match via :host-context selectors.
   */
  #syncChildFieldHeight(): void {
    const current = this.getAttribute(attributes.FIELD_HEIGHT);

    if (this.alignItems !== 'center-inputs') {
      if (current !== null) this.removeAttribute(attributes.FIELD_HEIGHT);
      return;
    }

    const inputs = this.querySelectorAll<HTMLElement>(
      'ids-input, ids-date-picker, ids-time-picker, ids-dropdown, ids-textarea, ids-trigger-field'
    );
    let detected: string | null = null;
    inputs.forEach((el) => {
      if (detected) return;
      const fh = el.getAttribute(attributes.FIELD_HEIGHT);
      if (fh) {
        detected = fh;
      } else if (el.hasAttribute(attributes.COMPACT)) {
        detected = 'xs';
      }
    });

    if (detected) {
      if (current !== detected) this.setAttribute(attributes.FIELD_HEIGHT, detected);
    } else if (current !== null) {
      this.removeAttribute(attributes.FIELD_HEIGHT);
    }
  }

  #observeChildren(): void {
    this.#childObserver = new MutationObserver((records) => {
      // Ignore mutations on the host itself to avoid feedback loops when
      // we reflect the inferred field-height back onto this element.
      const fromChild = records.some((r) => r.target !== this);
      if (fromChild) this.#syncChildFieldHeight();
    });
    this.#childObserver.observe(this, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: [attributes.FIELD_HEIGHT, attributes.COMPACT]
    });
  }
}
